import React, { useEffect, useMemo, useState } from "react";
/* eslint-disable react/no-unescaped-entities */
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, ConfirmDeleteButton } from "./_helpers";
import MapPicker from "@/components/admin/MapPicker";
import MediaPicker from "@/components/admin/MediaPicker";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, Phone, User as UserI, MapPin, Clock, FileText, Image as ImageI, Send, Newspaper, X, Search } from "lucide-react";

const emptyForm = {
  title: "", description: "", content_html: "", event_type: "", start_at: "", end_at: "",
  expire_at: "",
  venue: "", address: "", lat: "", lng: "", province_id: "",
  contact_name: "", contact_phone: "", website: "",
  cover_image: "", gallery: [],
  is_active: true, is_featured: false, status: "approved",
};

export default function AdminEvents() {
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [tab, setTab] = useState("pending"); // pending first to surface moderation queue
  const [publishingEvent, setPublishingEvent] = useState(null);
  // Admin UX: unlimited listing + province filter + keyword search
  const [filterProvince, setFilterProvince] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce free-text search to avoid thrashing the backend on each keypress.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = async () => {
    try {
      // limit=0 → backend treats as "unlimited" (bounded by its hard cap).
      const base = `admin=true&include_archived=${showArchived}&limit=0`;
      const prov = filterProvince ? `&province_id=${filterProvince}` : "";
      const q = debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : "";
      const [appr, pend] = await Promise.all([
        api.get(`/events?status=approved&${base}${prov}${q}`),
        api.get(`/events?status=pending&${base}${prov}${q}`),
      ]);
      setApproved(appr.data);
      setPending(pend.data);
    } catch (e) {
      toast.error("Yüklenemedi");
    }
  };

  useEffect(() => { load(); }, [showArchived, filterProvince, debouncedQuery]);
  useEffect(() => {
    let cancelled = false;
    api.get("/provinces?active_only=false").then((r) => {
      if (!cancelled) setProvinces(r.data);
    });
    return () => { cancelled = true; };
  }, []);

  const provLookup = useMemo(() => {
    const m = {};
    provinces.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [provinces]);

  const resetForm = () => {
    setEditingId(null);
    setF(emptyForm);
  };

  const save = async () => {
    if (!f.title?.trim() || !f.start_at) return toast.error("Ad ve başlangıç tarihi zorunlu");
    const payload = {
      ...f,
      province_id: f.province_id || null,
      lat: f.lat !== "" && f.lat !== null ? +f.lat : null,
      lng: f.lng !== "" && f.lng !== null ? +f.lng : null,
      start_at: new Date(f.start_at).toISOString(),
      end_at: f.end_at ? new Date(f.end_at).toISOString() : null,
      expire_at: f.expire_at ? new Date(f.expire_at).toISOString() : null,
    };
    try {
      if (editingId) await api.put(`/events/${editingId}`, payload);
      else await api.post("/events", payload);
      toast.success(editingId ? "Güncellendi" : "Eklendi");
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setF({
      ...emptyForm,
      ...e,
      lat: e.lat ?? "",
      lng: e.lng ?? "",
      start_at: e.start_at ? e.start_at.slice(0, 16) : "",
      end_at: e.end_at ? e.end_at.slice(0, 16) : "",
      expire_at: e.expire_at ? e.expire_at.slice(0, 16) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const approve = async (id) => {
    try {
      await api.post(`/events/${id}/approve`);
      toast.success("Etkinlik onaylandı ve yayına alındı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Bu etkinlik bildirimi reddedilecek. Emin misiniz?")) return;
    try {
      await api.post(`/events/${id}/reject`);
      toast.success("Etkinlik reddedildi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddedilemedi");
    }
  };

  const isExpired = (e) => e.expire_at && new Date(e.expire_at) < new Date();
  const list = tab === "pending" ? pending : approved;

  return (
    <div data-testid="admin-events-page">
      <AdminPageHeader
        title="Etkinlikler"
        subtitle="Vatandaşlardan gelen etkinlik bildirimlerini buradan onaylayabilir ya da reddedebilirsiniz."
        action={
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-1.5 border border-gray-200 rounded-md bg-white">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 accent-[#e11d2a]"
              data-testid="admin-events-archive-toggle"
            />
            <span>Süresi geçenleri göster</span>
          </label>
        }
      />

      {/* Filter bar: province + keyword search. Both work together. */}
      <div className="flex flex-col md:flex-row gap-2 mb-3" data-testid="admin-events-filterbar">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Etkinliklerde ara (başlık, açıklama, mekan, adres)…"
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#e11d2a]/30 focus:border-[#e11d2a]"
            data-testid="admin-events-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded"
              title="Aramayı temizle"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>
        <div className="md:w-64">
          <select
            value={filterProvince}
            onChange={(e) => setFilterProvince(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#e11d2a]/30 focus:border-[#e11d2a]"
            data-testid="admin-events-province-filter"
          >
            <option value="">Tüm iller</option>
            {provinces.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        {(filterProvince || debouncedQuery) && (
          <button
            type="button"
            onClick={() => { setFilterProvince(""); setQuery(""); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-xs hover:bg-gray-50 whitespace-nowrap"
            data-testid="admin-events-filters-clear"
          >
            Filtreleri Temizle
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        <TabButton
          active={tab === "pending"}
          onClick={() => setTab("pending")}
          testId="admin-events-tab-pending"
        >
          <Inbox className="w-4 h-4" /> Onay Bekleyenler
          {pending.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#e11d2a] text-white">
              {pending.length}
            </span>
          )}
        </TabButton>
        <TabButton
          active={tab === "approved"}
          onClick={() => setTab("approved")}
          testId="admin-events-tab-approved"
        >
          <CheckCircle2 className="w-4 h-4" /> Onaylı Etkinlikler ({approved.length})
        </TabButton>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* List */}
        <div
          className="bg-white border border-gray-100 rounded-lg p-4 max-h-[700px] overflow-y-auto"
          data-testid={`admin-events-${tab}-list`}
        >
          <div className="text-[11px] text-gray-500 mb-2 flex items-center justify-between">
            <span>
              Toplam <strong>{list.length}</strong> kayıt
              {(filterProvince || debouncedQuery) && " (filtreli)"}
            </span>
          </div>
          {list.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-10">
              {tab === "pending"
                ? "Onay bekleyen bildirim yok"
                : (filterProvince || debouncedQuery)
                  ? "Filtrelere uyan etkinlik bulunamadı"
                  : "Henüz onaylı etkinlik yok"}
            </p>
          )}

          {list.map((e) => (
            <div
              key={e.id}
              className={`py-3 px-2 -mx-2 border-b last:border-0 ${
                tab === "pending" ? "bg-amber-50/40 rounded-md mb-1.5 border-b-0" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                    <span className="truncate max-w-[260px]">{e.title}</span>
                    {isExpired(e) && (
                      <span className="text-[9px] uppercase font-bold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                        Arşiv
                      </span>
                    )}
                    {e.event_type && (
                      <span className="text-[10px] uppercase bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {e.event_type}
                      </span>
                    )}
                    {e.province_id && provLookup[e.province_id] && (
                      <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                        {provLookup[e.province_id]}
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(e.start_at).toLocaleString("tr-TR")}
                    {e.venue ? ` · ${e.venue}` : ""}
                  </div>

                  {e.address && (
                    <div className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                      <span className="line-clamp-1">{e.address}</span>
                      {e.lat != null && e.lng != null && (
                        <a
                          href={`https://www.google.com/maps?q=${e.lat},${e.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline ml-1 shrink-0"
                        >
                          haritada
                        </a>
                      )}
                    </div>
                  )}

                  {(e.contact_name || e.contact_phone) && (
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-3">
                      {e.contact_name && (
                        <span className="flex items-center gap-1">
                          <UserI className="w-3 h-3" />
                          {e.contact_name}
                        </span>
                      )}
                      {e.contact_phone && (
                        <a
                          href={`tel:${e.contact_phone.replace(/\s/g, "")}`}
                          className="flex items-center gap-1 hover:text-[#e11d2a]"
                        >
                          <Phone className="w-3 h-3" />
                          {e.contact_phone}
                        </a>
                      )}
                    </div>
                  )}

                  {tab === "pending" && e.description && (
                    <p className="text-xs text-gray-600 mt-2 line-clamp-3 italic border-l-2 border-amber-300 pl-2">
                      “{e.description}”
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end mt-2 pt-1.5 border-t border-gray-100">
                {tab === "pending" && (
                  <>
                    <button
                      onClick={() => approve(e.id)}
                      data-testid={`admin-event-approve-${e.id}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded font-semibold"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Onayla
                    </button>
                    <button
                      onClick={() => reject(e.id)}
                      data-testid={`admin-event-reject-${e.id}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reddet
                    </button>
                  </>
                )}
                {tab === "approved" && (
                  e.published_news_slug ? (
                    <a
                      href={`/haber/${e.published_news_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded font-semibold"
                      title="Bu etkinlik habere dönüştürüldü"
                    >
                      <Newspaper className="w-3.5 h-3.5" /> Yayında
                    </a>
                  ) : (
                    <button
                      onClick={() => setPublishingEvent(e)}
                      data-testid={`admin-event-publish-${e.id}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-[#e11d2a] hover:bg-[#c01825] text-white rounded font-semibold"
                      title="Bu etkinliği bir haber kartı olarak yayınla"
                    >
                      <Send className="w-3.5 h-3.5" /> Habere Dönüştür
                    </button>
                  )
                )}
                <button
                  onClick={() => openEdit(e)}
                  className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50"
                  data-testid={`admin-event-edit-${e.id}`}
                >
                  Düzenle
                </button>
                <ConfirmDeleteButton
                  onConfirm={async () => {
                    await api.delete(`/events/${e.id}`);
                    load();
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2 max-h-[700px] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">{editingId ? "Etkinliği Düzenle" : "Yeni Etkinlik"}</h3>
            {editingId && (
              <button onClick={resetForm} className="text-xs text-gray-500 hover:underline">
                İptal
              </button>
            )}
          </div>
          <Field label="Ad *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Field label="Tip" value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Başlangıç *"
              type="datetime-local"
              value={f.start_at}
              onChange={(e) => setF({ ...f, start_at: e.target.value })}
            />
            <Field
              label="Bitiş"
              type="datetime-local"
              value={f.end_at}
              onChange={(e) => setF({ ...f, end_at: e.target.value })}
            />
          </div>
          <div>
            <Field
              label="Yayından Kaldırma Tarihi (Arşivle)"
              type="datetime-local"
              value={f.expire_at}
              onChange={(e) => setF({ ...f, expire_at: e.target.value })}
              data-testid="admin-event-expire-at"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">
              Bu tarihten sonra önyüzde görünmez. Boş bırakılırsa süresiz görünür.
            </p>
          </div>
          <Field label="Mekan" value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} />
          <Field label="Adres" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600 flex items-center gap-1">
              <ImageI className="w-3.5 h-3.5" /> Kapak Görseli
            </label>
            <MediaPicker
              value={f.cover_image}
              onChange={(url) => setF({ ...f, cover_image: url })}
              data-testid="admin-event-cover"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600 flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Detay İçeriği
            </label>
            <textarea
              value={f.content_html || ""}
              onChange={(e) => setF({ ...f, content_html: e.target.value })}
              rows={6}
              placeholder="Etkinliğin detaylı tanıtım metni (uzun açıklama, program, biletleme bilgileri vb.)"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
              data-testid="admin-event-content"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">
              Kısa açıklama kart önizlemelerinde, detay içeriği etkinlik detay sayfasında ve "habere dönüştür" akışında kullanılır.
              💡 YouTube/Vimeo linkini <strong>kendi satırına</strong> yapıştırın → otomatik video oynatıcı olarak yerleşir.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Görsel Galerisi</label>
            <div className="grid grid-cols-3 gap-2">
              {(f.gallery || []).map((url, i) => (
                <div key={i} className="relative aspect-square border border-gray-200 rounded overflow-hidden group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setF({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) })}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                    title="Kaldır"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div className="aspect-square">
                <MediaPicker
                  value=""
                  onChange={(url) => url && setF({ ...f, gallery: [...(f.gallery || []), url] })}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Konum (haritadan seçin)</label>
            <MapPicker
              lat={f.lat}
              lng={f.lng}
              onChange={({ lat, lng }) => setF({ ...f, lat: lat ?? "", lng: lng ?? "" })}
              defaultQuery={[
                f.venue,
                f.address,
                provinces.find((p) => p.id === f.province_id)?.name,
              ]
                .filter(Boolean)
                .join(" ")}
              height={260}
            />
          </div>
          <Select
            label="İl"
            value={f.province_id || ""}
            options={[
              { value: "", label: "-" },
              ...provinces.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(e) => setF({ ...f, province_id: e.target.value })}
          />
          <button
            onClick={save}
            className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold hover:bg-[#c01825]"
            data-testid="admin-event-save"
          >
            {editingId ? "Güncelle" : "Ekle"}
          </button>
        </div>
      </div>

      {publishingEvent && (
        <PublishAsNewsModal
          event={publishingEvent}
          onClose={() => setPublishingEvent(null)}
          onPublished={async () => {
            setPublishingEvent(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function PublishAsNewsModal({ event, onClose, onPublished }) {
  const [isSlider, setIsSlider] = useState(false);
  const [priority, setPriority] = useState(0);
  const [isBreaking, setIsBreaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const publish = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/events/${event.id}/publish-as-news`, {
        is_in_slider: isSlider,
        slider_priority: Number(priority) || 0,
        is_breaking: isBreaking,
      });
      toast.success(
        `Habere dönüştürüldü → /haber/${r.data.news_slug}${isBreaking ? " (Push gönderildi)" : ""}`,
        { duration: 6000 }
      );
      await onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dönüştürülemedi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="publish-event-modal"
    >
      <div
        className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-black text-lg flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#e11d2a]" /> Habere Dönüştür
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              "<strong>{event.title}</strong>" etkinliği bir haber kartı olarak yayınlanacak.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSlider}
              onChange={(e) => setIsSlider(e.target.checked)}
              className="w-4 h-4 accent-[#e11d2a]"
              data-testid="publish-slider-toggle"
            />
            <span className="text-sm">Ana sayfa slider'ında göster</span>
          </label>

          {isSlider && (
            <div className="pl-6">
              <label className="block text-xs text-gray-600 mb-1">
                Slider Sırası (1 = ilk gösterilecek; boş/0 = en sona)
              </label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm"
                min={0}
                max={9999}
                data-testid="publish-slider-priority"
              />
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-gray-100">
            <input
              type="checkbox"
              checked={isBreaking}
              onChange={(e) => setIsBreaking(e.target.checked)}
              className="w-4 h-4 accent-[#e11d2a]"
              data-testid="publish-breaking-toggle"
            />
            <span className="text-sm">🔴 Son Dakika olarak yayınla (Push bildirimi gönder)</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded hover:bg-gray-50">
            İptal
          </button>
          <button
            onClick={publish}
            disabled={submitting}
            data-testid="publish-confirm-btn"
            className="px-4 py-2 text-sm bg-[#e11d2a] hover:bg-[#c01825] text-white rounded font-semibold disabled:opacity-50 flex items-center gap-1"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Yayınlanıyor..." : "Yayınla"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-[2px] transition-colors ${
        active
          ? "border-[#e11d2a] text-[#e11d2a]"
          : "border-transparent text-gray-600 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}
